import { track } from '@cartwheel/analytics'
import { Announcement, AnnouncementType, Card } from '@cartwheel/components'

export type ProBonoUtilizationCardProps = {
  utilized: number
  remaining: number
  total: number
  approved: number // approved (awaiting scheduling)
  program_manager_email: string
  user_name?: string
  user_roles?: string[]
  district_name?: string
  district_id?: string
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))
const clampPercent = (value: number) => clamp(value, 0, 100)

export default function ProBonoUtilizationCard({
  utilized,
  remaining,
  total,
  approved,
  program_manager_email: programManagerEmail,
  user_name: userName,
  user_roles: userRoles,
  district_name: districtName,
  district_id: districtId,
}: ProBonoUtilizationCardProps) {
  const safeTotal = total > 0 ? total : 0
  // Prevent weirdness if the API ever sends negatives or inconsistent counts
  const safeUtilized = clamp(utilized, 0, safeTotal)
  const safeApproved = clamp(approved, 0, safeTotal - safeUtilized)

  // Claimed spots are anything not remaining (utilized + approved awaiting scheduling)
  const claimed = safeUtilized + safeApproved

  // Remaining should agree with claimed from a UI perspective (still show the passed-in number in text,
  // but clamp for calculations so the bar is always consistent)
  const safeRemainingForBar = clamp(safeTotal - claimed, 0, safeTotal)

  const utilizedPercent = clampPercent(safeTotal > 0 ? (safeUtilized / safeTotal) * 100 : 0)
  const approvedPercentRaw = clampPercent(safeTotal > 0 ? (safeApproved / safeTotal) * 100 : 0)

  const claimedPercent = clampPercent(safeTotal > 0 ? (claimed / safeTotal) * 100 : 0)

  // Ensure the approved segment never exceeds the remaining space after utilized in the bar
  const approvedPercent = clampPercent(Math.min(approvedPercentRaw, 100 - utilizedPercent))

  const showFullyUtilizedCallout = safeRemainingForBar <= 0
  const showOneSpotRemainingCallout = safeTotal > 1 && safeRemainingForBar === 1

  const onClickCSM = () => {
    track('Click Email CSM', {
      userName: userName,
      userRoles: userRoles,
      district: districtName,
      districtId: districtId,
      programManagerEmail: programManagerEmail,
      reason: showFullyUtilizedCallout ? 'pro_bono_full' : 'pro_bono_approaching_utilization',
    })
  }

  return (
    <Card.Root variant="outline" className="gap-10 p-6 h-full">
      <Card.Header>
        <div className="flex items-start gap-2">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white-dark">
            <i
              className="fa-sharp fa-solid fa-handshake-angle text-[20px] text-indigo"
              aria-hidden="true"
            />
          </div>
          <div className="flex flex-col">
            <div className="text-xl font-semibold text-charcoal">Pro Bono Spots</div>
            <div className="text-sm font-normal text-charcoal-50 line-clamp-2">
              You're making care possible for students who need it most. Need more spots?{' '}
              <a
                className="underline text-indigo"
                href={`mailto:${programManagerEmail}`}
                onClick={onClickCSM}
              >
                your CSM can help
              </a>
              .
            </div>
          </div>
        </div>
      </Card.Header>

      <Card.Content className="flex flex-col gap-2">
        <div className="relative h-[18px] w-full overflow-hidden rounded-[12px] bg-charcoal-25/20">
          {/* Green = claimed (utilized + approved awaiting scheduling) */}
          <div
            data-testid="pro-bono-claimed-bar"
            className="absolute inset-y-0 left-0 h-full bg-forest-light"
            style={{ width: `${claimedPercent}%` }}
          />

          {/* Blue = approved awaiting scheduling segment INSIDE green, starting after utilized */}
          {safeApproved > 0 ? (
            <div
              data-testid="pro-bono-approved-bar"
              className="absolute inset-y-0 h-full bg-sea"
              style={{ left: `${utilizedPercent}%`, width: `${approvedPercent}%` }}
            />
          ) : null}
        </div>

        <div className="flex items-center justify-end text-sm text-charcoal-75">{safeTotal}</div>
      </Card.Content>

      <div className="flex flex-wrap gap-6 text-sm text-charcoal-75">
        <div className="flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-full bg-forest-light" aria-hidden="true" />
          {safeUtilized} utilized
        </div>
        <div className="flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-full bg-sea" aria-hidden="true" />
          {safeApproved} approved (awaiting scheduling)
        </div>
        <div className="flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-full bg-charcoal-25/20" aria-hidden="true" />
          {Math.max(remaining, 0)} remaining
        </div>
      </div>

      {showFullyUtilizedCallout ? (
        <div className="-mt-3">
          <Announcement
            type={AnnouncementType.Success}
            title={<span className="text-base leading-6">Congratulations!</span>}
            message={
              <p className="text-base leading-6">
                You&apos;ve utilized all your pro bono slots.{' '}
                <a
                  className="underline"
                  href={`mailto:${programManagerEmail}`}
                  onClick={onClickCSM}
                >
                  Contact your CSM for next steps
                </a>
                .
              </p>
            }
            icon="fa-party-horn"
          />
        </div>
      ) : null}

      {showOneSpotRemainingCallout ? (
        <div className="-mt-3">
          <Announcement
            type={AnnouncementType.Success}
            title={<span className="text-base leading-6">Congratulations!</span>}
            message={
              <p className="text-base leading-6">
                You have 1 remaining pro bono slot.{' '}
                <a
                  className="underline"
                  href={`mailto:${programManagerEmail}`}
                  onClick={onClickCSM}
                >
                  Contact your CSM for next steps
                </a>
                .
              </p>
            }
            icon="fa-party-horn"
          />
        </div>
      ) : null}
    </Card.Root>
  )
}
